// What the Dock lists: Me's places (the parts' nav) and the host's place
// (cloud: a workspace's sections), in the order the person keeps on this
// device, and how many fit in the Dock's row.
//
// Promise:
// - Me's entries come first, then a line, then the place's (the Dock and its
//   menu alike). Switching the place changes only the place's entries.
// - The Dock takes the first entries of the kept order that are shown (not
//   hidden, and the place has them), as many as fit; within the Dock they are
//   drawn grouped (Me's, the line, the place's), each group in the kept order.
// - The order is kept by kind of entry (`me:<path>`, `place:<item id>`), the
//   same in every workspace. Entries the order does not know yet come at
//   their default position.
// - By default the Dock begins with three of Me's (Inbox, Cases, Agents;
//   People and Documents later) and the place's first two.
import type { ComponentType, ReactNode } from "react";
import type { DockPrefs } from "../shell/dock-prefs";
import type { ShellMenuItem } from "../shell/outside/contract";

export {
  type DockPrefs,
  dockPrefsSetting,
  parseDockPrefs,
} from "../shell/dock-prefs";

import {
  entryIcon,
  menuItems,
  type ShellMenuState,
} from "../shell/outside/source";

export interface DockEntry {
  /** `me:<path>` or `place:<item id>`. */
  key: string;
  group: "me" | "place";
  label: string;
  icon: ComponentType<{ className?: string }>;
  to: string;
  /** More paths where this entry is the current one. */
  paths?: readonly string[];
  useBadge?(): ReactNode;
  /** Around anything that calls `useBadge` (the place's context). */
  Scope?: ComponentType<{ children: ReactNode }>;
}

/** Me's entries that stay out of the Dock's first three by default (2026-10-03). */
const LATER = new Set(["me:/me/people", "me:/me/documents"]);

/** The default order: three of Me's, the place's first two, then the rest. */
export function defaultOrder(
  me: readonly DockEntry[],
  place: readonly DockEntry[],
): string[] {
  const meFirst = me.filter((e) => !LATER.has(e.key)).slice(0, 3);
  const firstKeys = new Set(meFirst.map((e) => e.key));
  return [
    ...meFirst.map((e) => e.key),
    ...place.slice(0, 2).map((e) => e.key),
    ...me.filter((e) => !firstKeys.has(e.key)).map((e) => e.key),
    ...place.slice(2).map((e) => e.key),
  ];
}

/**
 * The kept order, with keys it does not know put in at their default position
 * (right after the key before them in the default order).
 */
export function mergeOrder(
  kept: readonly string[],
  defaults: readonly string[],
): string[] {
  const out = kept.filter((k) => defaults.includes(k));
  defaults.forEach((key, i) => {
    if (out.includes(key)) return;
    const before = defaults
      .slice(0, i)
      .reverse()
      .find((k) => out.includes(k));
    out.splice(before === undefined ? 0 : out.indexOf(before) + 1, 0, key);
  });
  return out;
}

/** The Dock's entries from the frame's menu (shell/outside/source.tsx). */
export function dockEntries(state: ShellMenuState): {
  me: DockEntry[];
  place: DockEntry[];
} {
  const entry =
    (group: "me" | "place") =>
    (item: ShellMenuItem): DockEntry => {
      const extras = state.extras.get(item.id);
      return {
        key: item.id,
        group,
        label: item.label,
        icon: entryIcon(item, extras) ?? noIcon,
        to: item.to ?? "/me",
        ...(item.paths ? { paths: item.paths } : {}),
        ...(extras?.useBadge ? { useBadge: extras.useBadge } : {}),
        ...(extras?.Scope ? { Scope: extras.Scope } : {}),
      };
    };
  return {
    me: menuItems(state.menu, "me").map(entry("me")),
    place: menuItems(state.menu, "place").map(entry("place")),
  };
}

const noIcon = () => null;

export interface DockArrangement {
  /** Every shown entry, Me's then the place's, each in the kept order. */
  all: DockEntry[];
  /** The Dock's entries (the first `capacity` of the kept order), grouped. */
  dock: DockEntry[];
  /** The full kept order (the settings list it). */
  order: string[];
}

export function arrangeDock(
  me: readonly DockEntry[],
  place: readonly DockEntry[],
  prefs: DockPrefs,
  capacity: number,
): DockArrangement {
  const byKey = new Map([...me, ...place].map((e) => [e.key, e]));
  const order = mergeOrder(prefs.order, defaultOrder(me, place));
  const hidden = new Set(prefs.hidden);
  const shown = order
    .filter((k) => !hidden.has(k))
    .map((k) => byKey.get(k))
    .filter((e): e is DockEntry => e !== undefined);
  const grouped = (list: DockEntry[]) => [
    ...list.filter((e) => e.group === "me"),
    ...list.filter((e) => e.group === "place"),
  ];
  return {
    all: grouped(shown),
    dock: grouped(shown.slice(0, Math.max(0, capacity))),
    order,
  };
}

/** One entry's width in the Dock: the old web's floating button (49 px). */
export const DOCK_ITEM_WIDTH = 49;
/** The Dock's sides: 33 px from the screen's edges (Safari's own bar). */
export const DOCK_SIDE = 33;
/** Between the Dock and what sits beside it (the back button, a page's buttons). */
export const DOCK_GAP = 7;
/** The line between Me's entries and the place's. */
export const DOCK_LINE = 9;

/**
 * How many entries fit in a Dock row `lane` wide, besides "⋯" and the line
 * between the groups. Never less than one.
 */
export function dockCapacity(lane: number, withLine: boolean): number {
  const room = lane - 8 - (withLine ? DOCK_LINE : 0);
  return Math.max(1, Math.floor(room / DOCK_ITEM_WIDTH) - 1);
}

/**
 * How many entries a row `lane` wide holds: the line between the groups takes
 * room only when the entries that fit are of both groups.
 */
export function fitCapacity(
  lane: number,
  arrange: (capacity: number) => { dock: readonly DockEntry[] },
): number {
  const withLine = dockCapacity(lane, true);
  let capacity = dockCapacity(lane, false);
  while (capacity > withLine) {
    const { dock } = arrange(capacity);
    const both =
      dock.some((e) => e.group === "me") &&
      dock.some((e) => e.group === "place");
    if (!both) return capacity;
    capacity--;
  }
  return capacity;
}

/** Beside a page's buttons, the Dock rests only when this many entries fit. */
export const DOCK_BESIDE_MIN = 3;
